import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiEye, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const TradePartnerList = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => { loadPartners(); }, [typeFilter, statusFilter, searchQuery]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('tp_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      const res = await api.get(`/trade-partners?${params}`);
      setPartners(res.data || []);
    } catch (error) {
      toast.error('Failed to load trade partners');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/trade-partners/${id}`);
      toast.success('Trade partner deleted');
      setPartners(partners.filter(p => p.id !== id));
      setConfirmDelete(null);
    } catch (error) {
      toast.error('Failed to delete trade partner');
    }
  };

  const columns = [
    { key: 'code', label: 'Code', sortable: true },
    {
      key: 'name', label: 'Name', sortable: true,
      render: (v, row) => (
        <span style={{ color: '#0891b2', fontWeight: 500, cursor: 'pointer' }}
          onClick={() => navigate(`/trade-partner/${row.id}`)}>{v}</span>
      ),
    },
    { key: 'alias', label: 'Alias', sortable: true },
    { key: 'tp_type', label: 'Type', sortable: true },
    { key: 'contact_person', label: 'Contact', sortable: true },
    { key: 'tel', label: 'TEL', sortable: true },
    { key: 'address', label: 'Address', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    { key: 'state', label: 'State', sortable: true },
    { key: 'zip_code', label: 'Zip', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'tax_id', label: 'Tax ID / USCI No.', sortable: true },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (v) => (
        <span style={{
          padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
          background: v === 'Active' ? '#dcfce7' : '#fee2e2',
          color: v === 'Active' ? '#059669' : '#dc2626',
        }}>{v}</span>
      ),
    },
  ];

  const renderActions = (row) => [
    <button key="view" className="btn btn-sm btn-primary" onClick={() => navigate(`/trade-partner/${row.id}`)} title="View">
      <FiEye /> VIEW
    </button>,
    <button key="delete" className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(row)} title="Delete">
      <FiTrash2 />
    </button>,
  ];

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">TRADE PARTNER LIST</h2>
          <button className="btn btn-primary" onClick={() => navigate('/trade-partner/new')}>
            <FiPlus /> New Trade Partner
          </button>
        </div>
        <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
          <div className="filter-group">
            <label className="filter-label">Type:</label>
            <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Customer">Customer</option>
              <option value="Vendor">Vendor</option>
              <option value="Trucker">Trucker</option>
              <option value="Warehouse">Warehouse</option>
              <option value="Terminal">Terminal</option>
              <option value="Others">Others</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Status:</label>
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1, marginLeft: 'auto' }}>
            <input type="text" className="filter-input" placeholder="Search by code, name, alias, city, contact, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <DataTable columns={columns} data={partners} loading={loading} renderActions={renderActions} pageSize={20} />
      </div>
      <ConfirmDialog isOpen={!!confirmDelete} title="Delete Trade Partner" message={`Are you sure you want to delete ${confirmDelete?.name} (${confirmDelete?.code})?`} isDangerous onConfirm={() => handleDelete(confirmDelete.id)} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
};

export default TradePartnerList;
